import { Skeleton, useTheme } from '@mui/material';
import { AssetUrls } from 'config/assets';
import './Points.css';

const PLACEHOLDER_SIZES = (isBig: boolean) =>
  isBig ? { width: 96, height: 32 } : { width: 65, height: 19 };

interface Props {
  /**
   * `text` is the documented fallback on /brand: no pill, just the eternity
   * glyph and the gold number. Use it inside prose, where a value is quoted as
   * part of a sentence rather than displayed as a standalone card value — the
   * pill reads as a separate object there and breaks the line.
   */
  type?: 'default' | 'big' | 'small' | 'text';
  points?: number;
  isLoading?: boolean;
  isOver?: boolean;
}

/**
 * The arevakhach (U+058D). The coin icon IS this symbol, so the glyph
 * reproduces the same mark in text. See /brand.
 */
export const POINT_GLYPH = '֍';

// Points are integers everywhere; without this option Intl defaults to up to
// 3 fraction digits, letting fractional backend totals leak decimals into the UI.
const formatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export const Points = ({ type = 'default', points = 0, isLoading, isOver = false }: Props) => {
  const theme = useTheme();
  const pointsToString = formatter.format(points);
  const isBig = type === 'big';
  
  // Determine expansion class based on character count
  const getExpandClass = () => {
    const charCount = pointsToString.length;
    if (charCount < 8) return '';
    if (charCount <= 10) return 'expand'; // Keep original for backward compatibility
    if (charCount <= 13) return 'expand-md';
    if (charCount <= 16) return 'expand-lg';
    return 'expand-xl';
  };
  
  const expandClass = getExpandClass();
  const coinIconSizes = isBig
    ? { width: 21.89, height: 21.89 }
    : { width: 16, height: 16 };

  // Text fallback: the glyph and the number, no pill. Rendered as a span so it
  // sits inline in a sentence instead of interrupting it as a block.
  if (type === 'text') {
    return (
      <span
        className={`profile-points-text-only ${isOver ? 'over' : ''}`}
        data-testid="points-text"
      >
        <span className="profile-points-glyph" aria-hidden="true">
          {POINT_GLYPH}
        </span>
        <span className="profile-points-bare" data-testid="points-text-value">
          {pointsToString}
        </span>
      </span>
    );
  }

  if (isLoading) {
    return (
      <Skeleton
        variant="rounded"
        sx={{ backgroundColor: theme.colors.grey, borderRadius: 12 }}
        {...PLACEHOLDER_SIZES(isBig)}
      />
    );
  }

  return (
    <div className={`profile-points ${type} ${expandClass} ${isOver ? 'over' : ''}`}>
      <div className={`profile-points-content ${isBig ? 'big' : ''}`}>
        <img
          src={`${AssetUrls.base}/assets/icons/profile/coin.svg`}
          {...coinIconSizes}
          alt="coin-image"
        />
        <div className={`profile-points-text ${isBig ? 'big' : ''}`}>
          {pointsToString}
        </div>
      </div>
    </div>
  );
};
